import { serviceIntervals, suggestedServiceInterval } from '../api/types'
import { DeviceTypeFields } from './DeviceTypeFields'

/** En enhed, der tastes sammen med en ny sag (beslutning 109). Oprettes først, når sagen gemmes. */
export interface DeviceDraft {
  key: number
  name: string
  deviceType: string
  model: string
  manufacturer: string
  serialNumber: string
  location: string
  serviceIntervalMonths: string
}

let nextKey = 1

export function emptyDraft(): DeviceDraft {
  return {
    key: nextKey++,
    name: '',
    deviceType: '',
    model: '',
    manufacturer: '',
    serialNumber: '',
    location: '',
    serviceIntervalMonths: ''
  }
}

/** "Batteri 1" -> "Batteri 2", ellers " (2)" bagved - så fire ens batterier får hver sit navn. */
function nextName(name: string) {
  const match = /^(.*?)(\d+)$/.exec(name.trim())
  if (match) return `${match[1]}${Number(match[2]) + 1}`
  return name.trim() ? `${name.trim()} 2` : ''
}

/** Til serverens ProjectNewDeviceRequest. */
export function toNewDeviceRequest(draft: DeviceDraft) {
  return {
    name: draft.name.trim(),
    deviceType: draft.deviceType || null,
    model: draft.model || null,
    manufacturer: draft.manufacturer || null,
    serialNumber: draft.serialNumber || null,
    location: draft.location || null,
    serviceIntervalMonths: draft.serviceIntervalMonths ? Number(draft.serviceIntervalMonths) : null
  }
}

/**
 * Nye enheder på sagen, fx fire batterier (beslutning 109). Serienummeret kan mangle og rettes på enheden
 * bagefter. "Kopiér" laver en mage til med næste nummer i navnet og uden serienummer.
 */
export function NewDeviceDrafts({
  drafts,
  onChange
}: {
  drafts: DeviceDraft[]
  onChange: (drafts: DeviceDraft[]) => void
}) {
  function update(key: number, patch: Partial<DeviceDraft>) {
    onChange(
      drafts.map((draft) => {
        if (draft.key !== key) return draft
        const next = { ...draft, ...patch }
        // Et lovbestemt interval foreslås, når typen vælges - men kun når feltet står tomt.
        if (patch.deviceType !== undefined && !draft.serviceIntervalMonths) {
          const rule = suggestedServiceInterval(patch.deviceType)
          if (rule) next.serviceIntervalMonths = String(rule.months)
        }
        return next
      })
    )
  }

  function copy(draft: DeviceDraft) {
    const index = drafts.findIndex((d) => d.key === draft.key)
    const clone: DeviceDraft = { ...draft, key: nextKey++, name: nextName(draft.name), serialNumber: '' }
    onChange([...drafts.slice(0, index + 1), clone, ...drafts.slice(index + 1)])
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      {drafts.map((draft, index) => (
        <div key={draft.key} className="card card--flat" style={{ padding: 12 }}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
            <strong className="small">Enhed {index + 1}</strong>
            <div className="btn-group">
              <button type="button" className="btn btn--sm" onClick={() => copy(draft)}>
                Kopiér
              </button>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => onChange(drafts.filter((d) => d.key !== draft.key))}
              >
                Fjern
              </button>
            </div>
          </div>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">Navn *</span>
              <input
                required
                placeholder="f.eks. Batteri 1"
                value={draft.name}
                onChange={(e) => update(draft.key, { name: e.target.value })}
              />
            </label>

            <DeviceTypeFields
              deviceType={draft.deviceType}
              model={draft.model}
              manufacturer={draft.manufacturer}
              onChange={(next) => update(draft.key, next)}
            />

            <label className="field">
              <span className="field__label">Fabrikat</span>
              <input value={draft.manufacturer} onChange={(e) => update(draft.key, { manufacturer: e.target.value })} />
            </label>

            <label className="field">
              <span className="field__label">Serienummer</span>
              <input
                placeholder="Kan tilføjes senere"
                value={draft.serialNumber}
                onChange={(e) => update(draft.key, { serialNumber: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Placering i bygningen</span>
              <input
                placeholder="f.eks. Teknikrum, kælder"
                value={draft.location}
                onChange={(e) => update(draft.key, { location: e.target.value })}
              />
            </label>

            <label className="field">
              <span className="field__label">Service</span>
              <select
                value={draft.serviceIntervalMonths}
                onChange={(e) => update(draft.key, { serviceIntervalMonths: e.target.value })}
              >
                <option value="">Ingen serviceaftale</option>
                {serviceIntervals.map((interval) => (
                  <option key={interval.months} value={interval.months}>
                    {interval.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      ))}

      <div>
        <button type="button" className="btn btn--sm" onClick={() => onChange([...drafts, emptyDraft()])}>
          + Tilføj enhed
        </button>
      </div>
    </div>
  )
}
